import React, { useMemo } from 'react';
import { BsTrash } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Divider, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../components/Spinner';
import {
  getCompanyEmailTemplates, getCompanySignatureTemplates,
  selectCompanyEmailTemplates, selectCompanySignatureTemplates,
  setCurrentBodyTemplateName,
  setCurrentMessageHtml,
  setCurrentSignature, setCurrentSignatureTemplateName,
  setCurrentSubjectHtml,
  setSelectedCheckboxCodes
} from '../../../../../redux/modules/emails';
import { insuranceRequestDefaultTemplate } from '../../../../../redux/modules/emails/defaults';
import { selectEditPolicyTemplate } from '../../../../../redux/modules/insured';
import { showModal } from '../../../../../redux/modules/modal';
import ConfirmDeleteEmailTemplate from '../ConfirmDeleteEmailTemplate';

const CompanyEmailTemplates = ({
  type,
  templateWord,
  defaultTemplates = [],
  onSelect = () => {},
  ...props
}) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const companyEmailTemplates = useSelector(selectCompanyEmailTemplates);
  const companySignatureTemplates = useSelector(selectCompanySignatureTemplates);
  const editPolicyTemplate = useSelector(selectEditPolicyTemplate);

  const pinsTemplates = useMemo(() => {
    if (editPolicyTemplate) {
      return [insuranceRequestDefaultTemplate, editPolicyTemplate];
    }

    return [insuranceRequestDefaultTemplate, ...defaultTemplates];
  }, [editPolicyTemplate]);

  const dataSet = useMemo(
    () => (type === 'main_content'
      ? [...pinsTemplates, ...companyEmailTemplates.data]
      : companySignatureTemplates.data),
    [companyEmailTemplates, companySignatureTemplates, type]
  );

  const fetchMoreData = () => {
    if (type === 'main_content') {
      const pageNumber = companyEmailTemplates.currentPage + 1;
      dispatch(getCompanyEmailTemplates(companyId, { pageNumber, pageSize: 10 }));
    } else {
      const pageNumber = companySignatureTemplates.currentPage + 1;
      dispatch(getCompanySignatureTemplates(companyId, { pageNumber, pageSize: 10 }));
    }
  };

  const onTemplateSelect = template => {
    if (template.templateType === 'main_content') {
      dispatch(setCurrentBodyTemplateName(template.templateName));
      dispatch(setCurrentMessageHtml(template.templateInfo.html));
      dispatch(setCurrentSubjectHtml(template.templateInfo.subjectHtml));
      if (template.templateInfo.codeLetters) {
        dispatch(setSelectedCheckboxCodes(template.templateInfo.codeLetters));
      }
    } else if (template.templateType === 'signature') {
      dispatch(setCurrentSignature(template.templateInfo.html));
      dispatch(setCurrentSignatureTemplateName(template.templateName));
    }

    onSelect(template);
  };

  const openConfirmDeleteModal = template => {
    dispatch(showModal(() => (
      <ConfirmDeleteEmailTemplate
        companyId={companyId}
        template={template}
        type="company"
      />
    ), {
      actionRequired: true,
      buttons: {},
      center: true,
      size: 'sm',
      title: 'Delete Template?'
    }));
  };

  return (
    <Box {...props}>
      <Flex alignItems="center" flexFlow="row nowrap" h="50px" justifyContent="flex-start">
        <Text textStyle="bodyMd600">
          {`Company ${templateWord}s`}
        </Text>
      </Flex>
      <Divider />
      <Box className="companyTemplateScroll" minW="300px" my={2}>
        <InfiniteScroll
          dataLength={companyEmailTemplates.data.length}
          hasMore={companyEmailTemplates.currentPage !== companyEmailTemplates.lastPage}
          height="400px"
          loader={(<LoadingSpinner isInline />)}
          next={fetchMoreData}
          scrollableTarget="companyTemplateScroll"
          scrollThreshold={0.8}
        >
          {dataSet.map(temp => (
            <Flex
              _active={{ bgColor: 'greyScale.200' }}
              _hover={{ bgColor: 'greyScale.100' }}
              cursor="pointer"
              flexFlow="row nowrap"
              justifyContent="space-between"
              key={`${type}-${temp.templateId}`}
              p={2}
              textStyle="bodyMd500"
            >
              <Box flexBasis="90%" textAlign="left" onClick={() => onTemplateSelect(temp)}>
                <Text>{temp.templateName}</Text>
              </Box>
              <Box flexBasis="10%">
                <IconButton
                  icon={<Icon as={BsTrash} />}
                  size="sm"
                  variant="textBlack"
                  visibility={temp.templateId ? 'visible' : 'hidden'}
                  onClick={() => openConfirmDeleteModal(temp)}
                />
              </Box>
            </Flex>
          ))}

          {!dataSet?.length && (<Text>{`No ${templateWord}s`}</Text>)}
        </InfiniteScroll>
      </Box>
    </Box>
  );
};

CompanyEmailTemplates.propTypes = {
  type: PropTypes.oneOf(['main_content', 'signature']).isRequired,
  templateWord: PropTypes.string,
  defaultTemplates: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func
};

export default CompanyEmailTemplates;
